import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PortfolioProject {
  id: string;
  service_id: string;
  title: string;
  title_en?: string;
  title_ru?: string;
  description?: string;
  description_en?: string;
  description_ru?: string;
  image_url: string;
  project_url?: string;
  client_name?: string;
  completion_date?: string;
  technologies?: string[];
  is_featured: boolean;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

// Fetch all projects for a service
export const usePortfolioProjects = (serviceId: string) => {
  return useQuery({
    queryKey: ["portfolio-projects", serviceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_projects")
        .select("*")
        .eq("service_id", serviceId)
        .eq("is_active", true)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return data as PortfolioProject[];
    },
    enabled: !!serviceId,
  });
};

// Fetch all projects (for admin)
export const useAllPortfolioProjects = () => {
  return useQuery({
    queryKey: ["all-portfolio-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_projects")
        .select("*, services(title)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

// Create project
export const useCreatePortfolioProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project: Omit<PortfolioProject, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from("portfolio_projects")
        .insert([project])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolio-projects"] });
      queryClient.invalidateQueries({ queryKey: ["all-portfolio-projects"] });
      toast.success("Նախագիծը հաջողությամբ ավելացվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};

// Update project
export const useUpdatePortfolioProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PortfolioProject> & { id: string }) => {
      const { data, error } = await supabase
        .from("portfolio_projects")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolio-projects"] });
      queryClient.invalidateQueries({ queryKey: ["all-portfolio-projects"] });
      toast.success("Նախագիծը հաջողությամբ թարմացվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};

// Delete project
export const useDeletePortfolioProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("portfolio_projects")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portfolio-projects"] });
      queryClient.invalidateQueries({ queryKey: ["all-portfolio-projects"] });
      toast.success("Նախագիծը հաջողությամբ ջնջվեց");
    },
    onError: (error) => {
      toast.error("Սխալ՝ " + error.message);
    },
  });
};
